import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Envelope, Lock, User, ArrowRight } from "@phosphor-icons/react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.5 14.6 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 11.7S6.9 20.9 12 20.9c6.9 0 9.5-4.8 9.5-7.3 0-.5-.1-.9-.1-1.4H12z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="#fff">
    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
  </svg>
);

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const {
    loginWithEmail,
    signupWithEmail,
    loginWithGoogle,
    loginWithFacebook,
    isAuthenticated,
  } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from || "/app";

  useEffect(() => {
    if (isAuthenticated) nav(redirect, { replace: true });
  }, [isAuthenticated, nav, redirect]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isLogin) {
        await loginWithEmail(email, password);
        toast.success("Welcome back");
      } else {
        await signupWithEmail(email, password, name || email.split("@")[0]);
        toast.success("Account created");
      }
      nav(redirect, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (isLogin ? "Login failed" : "Signup failed");
      toast.error(typeof msg === "string" ? msg : "Auth failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0a192f] text-white">
      {/* Left visual */}
      <div className="hidden lg:flex relative overflow-hidden border-e border-white/10">
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(600px 400px at 20% 20%, rgba(16,185,129,0.25), transparent 60%), radial-gradient(600px 400px at 80% 80%, rgba(59,130,246,0.15), transparent 60%)",
          }}
        />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-3 text-white" aria-label="LoadWise home">
            <img
              src="/branding/loadwise-logo-192.png"
              alt="LoadWise"
              className="h-11 w-auto"
              style={{ filter: "drop-shadow(0 0 14px rgba(16,185,129,0.35))" }}
              draggable="false"
            />
            <span className="sr-only">LoadWise</span>
          </Link>

          <div className="max-w-md">
            <p className="text-emerald-300 text-xs uppercase tracking-[0.24em]">Load Screening OS</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight leading-tight">
              Book only the loads <span className="italic neon-text">that pay.</span>
            </h1>
            <p className="mt-5 text-slate-300 text-sm leading-relaxed">
              Real-time trip cost, driver pay, dispatcher fee and net profit — one verdict, in 3 seconds. Screen unlimited loads once you sign in.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <div>
                <p className="tabular text-2xl font-semibold text-white">3</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mt-1">free/day</p>
              </div>
              <div>
                <p className="tabular text-2xl font-semibold neon-text">∞</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mt-1">on Pro</p>
              </div>
              <div>
                <p className="tabular text-2xl font-semibold text-white">$19</p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mt-1">/ month</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500">© {new Date().getFullYear()} LoadWise</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2 text-white" aria-label="LoadWise home">
              <img
                src="/branding/loadwise-logo-192.png"
                alt="LoadWise"
                className="h-10 w-auto"
                style={{ filter: "drop-shadow(0 0 12px rgba(16,185,129,0.3))" }}
                draggable="false"
              />
              <span className="sr-only">LoadWise</span>
            </Link>
          </div>

          <h2 className="text-3xl font-semibold tracking-tight">
            {isLogin ? "Sign in" : "Create your account"}
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            {isLogin
              ? "Continue screening loads without limits."
              : "Get unlimited load screenings on the free tier when you sign in."}
          </p>

          <Button
            type="button"
            data-testid="btn-google-signin"
            onClick={loginWithGoogle}
            variant="outline"
            className="mt-8 w-full h-11 rounded-xl border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white"
          >
            <GoogleIcon />
            Continue with Google
          </Button>

          <Button
            type="button"
            data-testid="btn-facebook-signin"
            onClick={() => {
              try {
                loginWithFacebook();
              } catch (err) {
                if (err?.message === "facebook_not_configured") {
                  toast.info("Facebook login opens soon — coming with launch.");
                } else {
                  toast.error("Facebook login unavailable");
                }
              }
            }}
            className="mt-3 w-full h-11 rounded-xl text-white font-medium hover:brightness-110 transition"
            style={{ backgroundColor: "#1877F2", borderColor: "#1877F2" }}
          >
            <FacebookIcon />
            Continue with Facebook
          </Button>

          <div className="my-6 flex items-center gap-3">
            <Separator className="bg-white/10 flex-1" />
            <span className="text-xs uppercase tracking-[0.14em] text-slate-500">or</span>
            <Separator className="bg-white/10 flex-1" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Name</Label>
                <div className="relative">
                  <User size={16} weight="bold" className="absolute inset-y-0 start-3 my-auto text-slate-500" />
                  <Input
                    data-testid="input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-11 rounded-xl bg-white/[0.03] border-white/10 text-white ps-9"
                    placeholder="Your name"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Email</Label>
              <div className="relative">
                <Envelope size={16} weight="bold" className="absolute inset-y-0 start-3 my-auto text-slate-500" />
                <Input
                  data-testid="input-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-xl bg-white/[0.03] border-white/10 text-white ps-9"
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400">Password</Label>
              <div className="relative">
                <Lock size={16} weight="bold" className="absolute inset-y-0 start-3 my-auto text-slate-500" />
                <Input
                  data-testid="input-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 rounded-xl bg-white/[0.03] border-white/10 text-white ps-9"
                  placeholder="At least 8 characters"
                  minLength={isLogin ? undefined : 8}
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="btn-submit-auth"
              disabled={submitting}
              className="btn-neon w-full h-11 rounded-xl"
            >
              {submitting
                ? "Please wait..."
                : isLogin
                  ? "Sign in"
                  : "Create account"}
              <ArrowRight size={16} weight="bold" className="flip-x ms-1" />
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-400 text-center">
            {isLogin ? (
              <>
                Don't have an account?{" "}
                <Link
                  data-testid="link-to-signup"
                  to="/signup"
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  Sign up
                </Link>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <Link
                  data-testid="link-to-login"
                  to="/login"
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  Sign in
                </Link>
              </>
            )}
          </p>
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
